// FileUpload/index.tsx

import * as React from 'react'
import {
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Typography,
  LinearProgress,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'

import { Plus, Upload, Trash2, X } from 'lucide-react'
import { FileList } from './FileList'
import { useFileUpload } from './useFileUpload'
import { useSidebarContext } from '../../contexts/Old_SidebarContext'

interface FileUploadProps {
  setShowFileUploadBox: (show: boolean) => void
}

export function FileUpload({ setShowFileUploadBox }: FileUploadProps) {
  const [status, setStatus] = React.useState<{ message: string; type: string } | null>(null)
  const [apiResponded, setApiResponded] = React.useState(false)
  const { newChatClicked, setNewChatClicked } = useSidebarContext()

  const {
    files,
    uploading,
    progress,
    uploadedFiles,
    getFileSignature,
    addFiles,
    uploadAll,
    clearAll,
    cancelUpload,
    removeFile,
    totalSelectedSize,
    uploadedTotalSize,
    formatBytes,
    restoredFileMeta,
    staleFileNames,
    dismissStaleFiles,
    MAX_FILES,
    TOTAL_MAX_SIZE,
  } = useFileUpload({
    onStatus: (message, type) => {
      setStatus({ message, type })
      // Only set apiResponded to true if it's NOT the processing message
      if (!message.includes('Processing')) {
        setApiResponded(true)
      }
    },
    setApiResponded,
  })

  // Reset API state when upload begins
  React.useEffect(() => {
    if (uploading) {
      setApiResponded(false)
      setStatus(null)
    }
  }, [uploading])

  // Clear all files when new chat is clicked
  React.useEffect(() => {
    if (newChatClicked) {
      clearAll()
      setNewChatClicked(false)
    }
  }, [newChatClicked, clearAll, setNewChatClicked])

  // Success message appears immediately when progress reaches 100 (client-side upload finished)
  const uploadSuccess = progress === 100 && status?.type !== 'error'

  // Processing shows only AFTER upload hits 100 and BEFORE API responds
  const processing = progress === 100 && !apiResponded && status?.type !== 'error'

  return (
    <Box
      p={2}
      border='1px solid #ddd'
      borderRadius={2}
      sx={{ position: 'relative', backgroundColor: '#fff' }}
    >
      {/* MINIMIZE ICON */}
      <Tooltip title='Minimize'>
        <IconButton
          size='small'
          onClick={() => {
            setShowFileUploadBox(false)
          }}
          sx={{ zIndex: 1, position: 'absolute', top: 8, right: 8 }}
        >
          <CloseFullscreenIcon sx={{ color: 'red' }} />
        </IconButton>
      </Tooltip>
      <Box
        sx={{
          position: { xs: 'relative', sm: 'absolute' },
          top: { xs: 0, sm: 50 },
          left: { xs: 0, sm: 16 },
          padding: 0,
        }}
      >
        <Typography lineHeight={1} variant='subtitle1' fontWeight='bold' mb={1}>
          File Size Limit: 300MB.
        </Typography>

        <Typography lineHeight={1} variant='subtitle1' fontWeight='bold' mb={1}>
          Number of File (max): 30
        </Typography>
      </Box>

      <Typography variant='h6' fontWeight='bold' mb={1} textAlign='center'>
        File Upload (PDF only)
      </Typography>

      {/* BUTTON ROW */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent='center'
        alignItems='center'
        sx={{ my: 1, width: '100%' }}
      >
        <Button
          fullWidth={true}
          sx={{
            width: { xs: '100%', sm: 'auto', backgroundColor: '#f6c232' },
          }}
          component='label'
          variant='contained'
          startIcon={<Plus size={18} />}
          disabled={
            uploading || totalSelectedSize > TOTAL_MAX_SIZE || files.length >= MAX_FILES // 🚫 Disable when count exceeds limit
          }
        >
          Select Files
          <input
            type='file'
            accept='application/pdf'
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files) {
                addFiles(e.target.files)
                // Reset input value to allow selecting the same files again
                e.target.value = ''
              }
            }}
          />
        </Button>

        <Button
          variant='outlined'
          fullWidth={true}
          sx={{
            width: { xs: '100%', sm: 'auto' },
          }}
          startIcon={<Upload size={18} />}
          disabled={
            files.length === 0 ||
            uploading ||
            uploadSuccess ||
            totalSelectedSize > TOTAL_MAX_SIZE ||
            files.length > MAX_FILES // 🚫 Disable when count exceeds limit
          }
          onClick={uploadAll}
        >
          Upload
        </Button>

        <Button
          variant='outlined'
          color='error'
          fullWidth={true}
          sx={{
            width: { xs: '100%', sm: 'auto' },
          }}
          startIcon={<Trash2 size={18} />}
          disabled={(files.length === 0 && restoredFileMeta.length === 0) || uploading}
          onClick={async () => {
            await clearAll()
            // Use setTimeout to ensure state updates complete before closing
            setTimeout(() => {
              setShowFileUploadBox(false)
            }, 100)
          }}
        >
          Clear All
        </Button>

        {uploading && (
          <Button
            variant='outlined'
            color='warning'
            fullWidth={true}
            sx={{
              width: { xs: '100%', sm: 'auto' },
            }}
            startIcon={<X size={18} />}
            onClick={cancelUpload}
          >
            Cancel
          </Button>
        )}
      </Stack>

      <Divider sx={{ my: 1 }} />

      {/* STALE FILES INFO — shown after page refresh */}
      {staleFileNames.length > 0 && files.length === 0 && (
        <Box
          sx={{
            mt: 1,
            mb: 1,
            p: 1.5,
            backgroundColor: '#e3f2fd',
            borderRadius: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box>
            <Typography variant='body2' fontWeight={600} mb={0.5}>
              Previously selected files:
            </Typography>
            {staleFileNames.map((name) => (
              <Typography key={name} variant='body2' sx={{ ml: 1 }}>
                {name}
              </Typography>
            ))}
            <Typography variant='body2' color='error.main' fontWeight={600} mt={0.5}>
              Your browser was refreshed, so the previously selected files are no longer available.
            </Typography>
            <Typography variant='body2' color='success.dark' fontWeight={600}>
              Please re-select them and upload to continue.
            </Typography>
          </Box>
          <IconButton size='small' onClick={dismissStaleFiles}>
            <X size={16} />
          </IconButton>
        </Box>
      )}

      {/* FILE LIST */}
      <FileList
        files={files}
        uploadedFiles={uploadedFiles}
        getFileSignature={getFileSignature}
        onRemove={removeFile}
        totalSelectedSize={totalSelectedSize}
        uploadedTotalSize={uploadedTotalSize}
        formatBytes={formatBytes}
        totalLimit={TOTAL_MAX_SIZE}
        MAX_FILES={MAX_FILES}
        restoredFileMeta={restoredFileMeta}
      />

      {/* UPLOADING PROGRESS (<100%) */}
      {uploading && progress < 100 && (
        <Box mt={3}>
          <Typography variant='body2' mb={1}>
            Uploading… {progress}%
          </Typography>

          <LinearProgress
            variant='determinate'
            value={progress}
            sx={{ height: 10, borderRadius: 5 }}
          />
        </Box>
      )}

      {/* SUCCESS (immediately when upload hits 100%) */}
      {uploadSuccess && (
        <Box
          sx={{
            mt: 2,
            p: 1,
            backgroundColor: '#e2f3e0',
            borderRadius: 1,
          }}
        >
          <Typography color='success.main' fontWeight={600}>
            Files uploaded successfully.
          </Typography>
        </Box>
      )}

      {/* PROCESSING MESSAGE (after 100% but before API response) */}
      {processing && (
        <Box
          sx={{
            mt: 1.5,
            p: 1,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#fff4d1',
            borderRadius: 1,
            gap: 1,
          }}
        >
          <Typography variant='body2' fontWeight={500}>
            {files.length === 1
              ? 'Please hold on, your file is being processed'
              : 'Please hold on, your files are being processed'}
          </Typography>
          <CircularProgress size={20} thickness={6} sx={{ color: 'success.dark' }} />
        </Box>
      )}

      {/* ERROR MESSAGE */}
      {status?.type === 'error' && (
        <Box
          sx={{
            mt: 2,
            p: 1,
            backgroundColor: '#ffe1e1',
            borderRadius: 1,
          }}
        >
          <Typography color='error.main' fontWeight={600}>
            {status.message}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
